import React, { useState } from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { number, object, string } from "yup";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  useTheme,
} from "@mui/material";
import {
  HelpCircle,
} from 'lucide-react';
import { createFAQ } from "./FAQService";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import { tokens } from "../../theme";

const schema = object().shape({
  id: number().notRequired(),
  category: string().required("Category is required"),
  question: string().required("Question is required"),
  answer: string().required("Answer is required"),
  moreDetail: string().notRequired(),
});

const FAQForm = (props) => {
  const {
    faqToEdit,
    isUpdating = false,
    setState,
    toggleModal,
    updateFAQ,
  } = props;

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [, setApiError] = useState(false);
  const setIsLoading = useLoadingSpinner();

  const {
    clearErrors,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: faqToEdit?.id ?? null,
      category: faqToEdit?.category ?? "",
      question: faqToEdit?.question ?? "",
      answer: faqToEdit?.answer ?? "",
      moreDetail: faqToEdit?.moreDetail ?? "",
    },
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    setApiError(false);
    try {
      if (isUpdating) {
        await updateFAQ(data, setApiError, setState, setIsLoading);
      } else {
        await createFAQ(data, setApiError, setState, setIsLoading);
      }
      // Service handles success toast and setState toggle.
      // We assume flow continues if no error thrown.
      reset();
      toggleModal !== undefined && toggleModal(false);
    } catch (err) {
      console.error("Submission failed", err);
    }
  };

  const handleCancel = () => {
    clearErrors();
    reset();
    toggleModal !== undefined && toggleModal(false);
  };

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : "#FFFFFF",
        p: { xs: 3, md: 5 },
        borderRadius: "24px",
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
              <HelpCircle size={28} color={colors.greenAccent[500]} />
              <Typography variant="h3" fontWeight="700" sx={{ color: colors.grey[100], fontFamily: "Josefin Sans" }}>
                {isUpdating ? "Edit FAQ Entry" : "Create New FAQ"}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: colors.grey[300], mb: 2 }}>
              Manage your community questions and answers with clarity.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Category"
              placeholder="e.g., General, Billing, Booking"
              {...register("category")}
              error={!!errors.category}
              helperText={errors.category?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Question"
              multiline
              rows={2}
              {...register("question")}
              error={!!errors.question}
              helperText={errors.question?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Answer"
              multiline
              rows={4}
              {...register("answer")}
              error={!!errors.answer}
              helperText={errors.answer?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Additional Details (Optional)"
              multiline
              rows={3}
              {...register("moreDetail")}
              error={!!errors.moreDetail}
              helperText={errors.moreDetail?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCancel}
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                borderColor: colors.redAccent[500],
                color: colors.redAccent[500],
                "&:hover": { borderColor: colors.redAccent[600], bgcolor: `${colors.redAccent[500]}0D` }
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                bgcolor: colors.greenAccent[500],
                color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                "&:hover": { bgcolor: colors.greenAccent[400], boxShadow: `0 8px 16px ${colors.greenAccent[500]}33` }
              }}
            >
              {isUpdating ? 'Update FAQ' : 'Publish FAQ'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

FAQForm.propTypes = {
  faqToEdit: PropTypes.object,
  isUpdating: PropTypes.bool,
  setState: PropTypes.func,
  toggleModal: PropTypes.func,
  updateFAQ: PropTypes.func,
};

export default FAQForm;
